import React, { useState } from "react";
import {
  Form,
  Input,
  Select,
  Button,
  Table,
  Tag,
  Space,
  Tooltip,
  Upload,
  Modal,
  Spin,
  theme,
} from "antd";
import {
  PlusOutlined,
  CloudUploadOutlined,
  FileTextOutlined,
  DownloadOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { SectionCard } from "../../Common/Layout";
import PracticeDataRequestService, {
  CsvTemplateType,
  CsvTemplateOptions,
  getEnumTypeValue,
  IValidationResult,
} from "../../Services/PracticeDataRequestService";
import { adminContext } from "../../Common/PageRoute";
import CsvValidationModal from "./CsvValidationModal";
import {
  normalizeFile,
  sortDataConversionFiles,
} from "./PracticeDataRequestFileUtils";

const { Option } = Select;
const { Dragger } = Upload;

interface PracticeDataRequestFilesProps {
  form: any;
  isEditing?: boolean;
  requestId?: string;
}

const PracticeDataRequestFiles: React.FC<PracticeDataRequestFilesProps> = ({
  form,
  isEditing,
  requestId,
}) => {
  const { token } = theme.useToken();
  const { openNotification } = React.useContext(adminContext);
  const [addFileModal, setAddFileModal] = useState<boolean>(false);
  const [newFileType, setNewFileType] = useState<CsvTemplateType>(
    CsvTemplateOptions[0].value,
  );
  const [newFileName, setNewFileName] = useState<string>("");
  const [newFileUploading, setNewFileUploading] = useState<boolean>(false);
  const [validationModalOpen, setValidationModalOpen] =
    useState<boolean>(false);
  const [activeValidationResult] = useState<IValidationResult | null>(null);

  // Handle adding a new data conversion file
  const handleAddNewConversionFile = async (file: File) => {
    setNewFileUploading(true);
    try {
      const currentFiles = form.getFieldValue("dataConversionFiles") || [];
      const fileName = newFileName.trim() || file.name || newFileType;
      const fileType = getEnumTypeValue(newFileType);
      const updatedFiles = sortDataConversionFiles([
        ...currentFiles,
        {
          name: fileName,
          type: fileType,
          file: {
            name: file.name,
            path: URL.createObjectURL(file),
          },
          rawFile: file,
        },
      ]);
      form.setFieldsValue({ dataConversionFiles: updatedFiles as any });
      setNewFileName("");
      setAddFileModal(false);
      openNotification(
        "success",
        `${newFileType} file attached. Click Save Changes to save.`,
      );
    } catch {
      openNotification("error", "Error attaching file.");
    } finally {
      setNewFileUploading(false);
    }
  };

  // Downloading CSV template
  const handleDownloadCsvTemplate = (templateType?: string) => {
    const type = templateType || CsvTemplateOptions?.[0]?.value;
    if (!type) {
      openNotification("warning", "Please select a template first.");
      return;
    }
    if (type === CsvTemplateType.Other) {
      return;
    }
    PracticeDataRequestService.downloadCsvTemplate(type as CsvTemplateType);
  };

  // The DownloadFile endpoint is [Authorize]-protected, so saved files can't be reached via a
  // plain <a href>; they must be fetched as an authenticated blob instead.
  const handleDownloadConversionFile = (fileItem: any) => {
    if (!requestId) {
      openNotification("error", "Save the request before downloading files.");
      return;
    }
    const fileId = fileItem.file?.id || fileItem.subType || fileItem.name;
    if (!fileId) return;
    PracticeDataRequestService.downloadDataConversionFile(
      requestId,
      fileId,
      fileItem.name,
    );
  };

  const columns = [
    {
      title: "S.No",
      key: "index",
      width: 60,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: "File Name",
      dataIndex: "name",
      key: "name",
      render: (text: string, record: any, index: number) => {
        const fileItem = normalizeFile(
          form.getFieldValue(["dataConversionFiles", index]) || record,
        );
        const fileUrl = fileItem.file?.path;
        const isLocalPreview =
          fileUrl?.startsWith("blob:") || fileUrl?.startsWith("data:");
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <FileTextOutlined
              style={{ color: token.colorPrimary, fontSize: 15 }}
            />
            {fileUrl && isLocalPreview ? (
              <a
                href={fileUrl}
                target="_blank"
                rel="noreferrer"
                style={{ fontWeight: 600, color: token.colorPrimary }}
              >
                {fileItem.name || text}
              </a>
            ) : fileUrl ? (
              <a
                onClick={() => handleDownloadConversionFile(fileItem)}
                style={{
                  fontWeight: 600,
                  color: token.colorPrimary,
                  cursor: "pointer",
                }}
              >
                {fileItem.name || text}
              </a>
            ) : (
              <span style={{ fontWeight: 600, color: token.colorText }}>
                {fileItem.name || text}
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: "Data Type",
      dataIndex: "type",
      key: "type",
      render: (text: string, record: any, index: number) => {
        const fileItem = normalizeFile(
          form.getFieldValue(["dataConversionFiles", index]) || record,
        );
        const rawType = fileItem.type || text || "";
        const opt = CsvTemplateOptions.find(
          (o) => o.value === rawType || o.label === rawType,
        );
        let displayLabel = opt ? opt.label : rawType || "Other Data Import";
        if (fileItem.subType) {
          displayLabel += ` - ${fileItem.subType}`;
        }
        return (
          <Tag color="cyan" style={{ borderRadius: 10, fontWeight: 500 }}>
            {displayLabel}
          </Tag>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_: any, record: any, index: number) => {
        const fileItem = normalizeFile(
          form.getFieldValue(["dataConversionFiles", index]) || record,
        );
        const fileUrl = fileItem.file?.path;
        const isLocalPreview =
          fileUrl?.startsWith("blob:") || fileUrl?.startsWith("data:");
        return (
          <Space>
            {fileUrl && (
              <Tooltip title="Download File">
                {isLocalPreview ? (
                  <Button
                    size="small"
                    icon={<DownloadOutlined />}
                    href={fileUrl}
                    target="_blank"
                    rel="noreferrer"
                  />
                ) : (
                  <Button
                    size="small"
                    icon={<DownloadOutlined />}
                    onClick={() => handleDownloadConversionFile(fileItem)}
                  />
                )}
              </Tooltip>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <SectionCard
        title="Client Data Conversion Files"
        description="Spreadsheets submitted by the practice for data migration"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setAddFileModal(true)}
            size="small"
          >
            Upload File
          </Button>
        }
      >
        <Form.List name="dataConversionFiles">
          {(fields) => {
            if (fields.length === 0) {
              return (
                <div
                  style={{
                    textAlign: "center",
                    padding: "40px 0",
                    color: token.colorTextSecondary,
                  }}
                >
                  <CloudUploadOutlined
                    style={{
                      fontSize: 36,
                      color: token.colorPrimary,
                      marginBottom: 10,
                      opacity: 0.8,
                    }}
                  />
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: token.colorText,
                    }}
                  >
                    No data conversion files uploaded yet
                  </div>
                  <div style={{ fontSize: 12, marginTop: 4 }}>
                    Click "Upload File" above to attach a client spreadsheet.
                  </div>
                </div>
              );
            }

            const tableDataSource = fields.map(({ key, name }) => ({
              key,
              name,
            }));

            return (
              <Table
                dataSource={tableDataSource}
                columns={columns}
                pagination={false}
                size="small"
                bordered
                scroll={{ x: "max-content" }}
              />
            );
          }}
        </Form.List>
      </SectionCard>

      {/* Modal upload file */}
      <Modal
        title="Upload Data Conversion File"
        open={addFileModal}
        onCancel={() => setAddFileModal(false)}
        footer={null}
        destroyOnClose
        centered
      >
        <div style={{ marginTop: 12 }}>
          <div style={{ marginBottom: 14 }}>
            <label
              style={{
                display: "block",
                marginBottom: 6,
                fontWeight: 600,
                fontSize: 13,
                color: token.colorText,
              }}
            >
              Data Import Type
            </label>
            <Space.Compact style={{ width: "100%" }}>
              <Select
                style={{ width: "100%" }}
                value={newFileType}
                onChange={(val) => {
                  setNewFileType(val);
                  if (val !== CsvTemplateType.Other) setNewFileName("");
                }}
              >
                {CsvTemplateOptions.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {" "}
                    {opt.label}
                  </Option>
                ))}
              </Select>
              <Tooltip title={"Download template"}>
                <Button
                  icon={<DownloadOutlined />}
                  disabled={newFileType === CsvTemplateType.Other}
                  onClick={() => handleDownloadCsvTemplate(newFileType)}
                />
              </Tooltip>
            </Space.Compact>
          </div>
          {newFileType === CsvTemplateType.Other && (
            <div style={{ marginBottom: 16 }}>
              <label
                style={{
                  display: "block",
                  marginBottom: 6,
                  fontWeight: 600,
                  fontSize: 13,
                  color: token.colorText,
                }}
              >
                {" "}
                Custom File Name
              </label>
              <Input
                placeholder="e.g. Historical Trial Balance 2024"
                value={newFileName}
                onChange={(e) => setNewFileName(e.target.value)}
              />
            </div>
          )}

          <Dragger
            accept=".csv,.xlsx"
            maxCount={1}
            showUploadList={false}
            beforeUpload={(file) => {
              handleAddNewConversionFile(file as File);
              return Upload.LIST_IGNORE;
            }}
            disabled={newFileUploading}
            style={{ padding: "20px 0", background: token.colorFillAlter }}
          >
            {newFileUploading ? (
              <Spin
                indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
              />
            ) : (
              <>
                <CloudUploadOutlined
                  style={{
                    fontSize: 32,
                    color: token.colorPrimary,
                    marginBottom: 8,
                  }}
                />
                <p
                  style={{ margin: 0, fontWeight: 500, color: token.colorText }}
                >
                  Click or drag CSV / Excel file to upload
                </p>
                <p
                  style={{
                    margin: "4px 0 0 0",
                    color: token.colorTextSecondary,
                    fontSize: 12,
                  }}
                >
                  Supported formats: .csv, .xlsx
                </p>
              </>
            )}
          </Dragger>
        </div>
      </Modal>
      <CsvValidationModal
        open={validationModalOpen}
        result={activeValidationResult}
        onClose={() => setValidationModalOpen(false)}
      />
    </>
  );
};

export default PracticeDataRequestFiles;
